import { useEffect, useMemo, useRef, useId } from "react";
import { useOptionalActiveDetailPageContext } from "@/contexts/ActiveDetailPageContext";
import { useOptionalDetailPageTabs } from "@/contexts/DetailPageTabsContext";
import type { DocTypeSchema } from "@/lib/erp/meta";
import type { ActiveDetailPageApi } from "@/contexts/ActiveDetailPageContext";
import { getDocTitle } from "@/lib/erp/docTitle";
import { summarizeRecord } from "@/lib/erp/recordSummary";
import { useDetailScreenSection } from "@/features/ai-context/useDetailScreenSection";

type Doc = Record<string, any>;

/**
 * The SAVED document behind a detail form. Prefer the caller's `savedData` (the
 * page's server copy, e.g. originalData). Without it, snapshot formData whenever its
 * `name|modified` changes: Frappe bumps `modified` only on load/save, so edits in
 * between never reach the snapshot (unsaved values are never exposed).
 */
function useSavedDoc(formData: Doc, savedData: Doc | null | undefined) {
  const savedRef = useRef<Doc | null>(null);
  const savedKeyRef = useRef("");
  if (savedData !== undefined) {
    savedRef.current = savedData && savedData.name ? savedData : null;
  } else {
    const key = formData?.name ? `${formData.name}|${formData.modified ?? ""}` : "";
    if (key !== savedKeyRef.current) {
      savedKeyRef.current = key;
      savedRef.current = key ? formData : null;
    }
  }
  return savedRef;
}

/**
 * Hook to register a detail page as "active" — used by dev autofill tools and by
 * the page-aware AI reporter (plan 1968: which record, title, status, key fields, tab).
 *
 * Usage in detail pages:
 * ```tsx
 * useRegisterActiveDetailPage({
 *   doctype: "Item",
 *   metaSchema,
 *   formData,
 *   setFormData,
 *   savedData: originalData, // optional: the saved server copy
 * });
 * ```
 */
export function useRegisterActiveDetailPage({
  doctype,
  metaSchema,
  formData,
  setFormData,
  savedData,
  enabled = true,
}: {
  doctype: string;
  metaSchema: DocTypeSchema | null | undefined;
  formData: Record<string, any>;
  setFormData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  /** The saved server copy of the document (never live edits). Omit to derive it from formData. */
  savedData?: Record<string, any> | null;
  /** false = don't register (e.g. a wrapper whose child registers the same record) */
  enabled?: boolean;
}) {
  const registry = useOptionalActiveDetailPageContext();
  const register = registry?.register;
  const clear = registry?.clear;
  const notifyChange = registry?.notifyChange;
  const tabsContext = useOptionalDetailPageTabs();

  // Generate stable ID for this page instance (pure, render-safe)
  const reactId = useId();
  const pageId = useMemo(() => `${doctype}-${reactId}`, [doctype, reactId]);

  // Keep formData ref updated (avoids stale closures)
  const formDataRef = useRef(formData);
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  const savedRef = useSavedDoc(formData, savedData);
  const metaRef = useRef(metaSchema);
  metaRef.current = metaSchema;

  const activeTabId = tabsContext?.activeTab ?? "";
  const activeTabLabel = tabsContext?.tabs.find((t) => t.name === activeTabId)?.label;
  const tabRef = useRef<{ id: string; label?: string } | null>(null);
  tabRef.current = activeTabId ? { id: activeTabId, label: activeTabLabel } : null;

  const savedDoc = savedRef.current;
  const docName: string | null = savedDoc?.name ? String(savedDoc.name) : null;

  // Register/unregister when doctype, metaSchema or the saved record changes
  useEffect(() => {
    // Only register if meta is loaded
    if (!enabled || !metaSchema || !register || !clear) {
      return;
    }

    const api: ActiveDetailPageApi = {
      doctype,
      getMeta: () => metaSchema,
      getFormData: () => formDataRef.current,
      applyPatch: (patch: Record<string, any>) => {
        // Batched update: single setState call
        setFormData((prev) => ({ ...prev, ...patch }));
      },
      applyTablePatch: (fieldname: string, nextRows: any[]) => {
        // Update table field with new rows
        setFormData((prev) => ({ ...prev, [fieldname]: nextRows }));
      },
      isReady: !!metaSchema,
      docName,
      getTitle: () => getDocTitle(metaRef.current, savedRef.current),
      getRecordSummary: () => summarizeRecord(metaRef.current, savedRef.current),
      getActiveTab: () => tabRef.current,
    };

    register(api, pageId);

    // Cleanup: unregister on unmount or when doctype changes
    return () => {
      clear(pageId);
    };
  }, [doctype, metaSchema, register, clear, setFormData, pageId, enabled, docName, savedRef]);

  // Page-aware AI screen snapshot: the visible tab's fields, SAVED values only (read at capture)
  useDetailScreenSection({ enabled: enabled && !!metaSchema, tabs: tabsContext?.tabs, activeTab: activeTabId, savedRef });

  // Title / status / tab changed without a re-register -> tell listeners (no re-render)
  const signature = [
    savedDoc?.modified ?? "",
    getDocTitle(metaSchema, savedDoc) ?? "",
    savedDoc?.docstatus ?? "",
    savedDoc?.status ?? "",
    activeTabId,
  ].join("|");
  const lastSignatureRef = useRef(signature);
  useEffect(() => {
    if (lastSignatureRef.current === signature) return;
    lastSignatureRef.current = signature;
    notifyChange?.(pageId);
  }, [signature, notifyChange, pageId]);
}
